import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const YouTubePlayerModal = ({ isOpen, onClose, video }) => {
    // Close modal on ESC key press
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!video) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                            {/* Header */}
                            <div className="flex justify-between items-start p-4 bg-gray-800">
                                <div className="flex-1 pr-4">
                                    <h3 className="text-lg font-semibold text-white line-clamp-1">
                                        {video.title}
                                    </h3>
                                    {video.duration && (
                                        <span className="text-sm text-gray-400">
                                            Duration: {video.duration}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-white hover:bg-gray-700 rounded-full p-2 transition-colors"
                                    aria-label="Close video player"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Video Player */}
                            <div className="relative w-full aspect-video bg-black">
                                <iframe
                                    src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0`}
                                    title={video.title}
                                    className="absolute inset-0 w-full h-full"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                />
                            </div>

                            {/* Description */}
                            {video.description && (
                                <div className="p-4 bg-gray-800 border-t border-gray-700">
                                    <p className="text-sm text-gray-300 line-clamp-3">
                                        {video.description}
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default YouTubePlayerModal;
